import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { loadGoogleMaps } from '@theme/google-maps';

const EARTH_RADIUS_KM = 6371;

/**
 * Great-circle distance between two lat/lng points, in kilometres.
 * @param {{ lat: number, lng: number }} a - The first point.
 * @param {{ lat: number, lng: number }} b - The second point.
 * @returns {number} The distance between the two points, in kilometres.
 */
function haversineDistanceKm(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/**
 * The "Find in store" PDP widget: a button that opens a popup listing physical stores where the
 * selected variant is available for pickup, with a per-store detail view.
 * @extends {Component<{}>}
 */
class FindInStore extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;

  /** @type {HTMLElement | null} */
  #dialog = null;

  /** @type {EventTarget | null} */
  #closestSection = null;

  /** @type {(event: ProductSelectEvent) => void} */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) this.dataset.productUrl = newProduct.url;

        const variantId = resource ? resource.id : null;
        if (variantId && variantId !== this.dataset.variantId) {
          this.dataset.variantId = variantId;
          this.#fetchAvailability(variantId);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[find-in-store] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    // Portal the sibling dialog to document.body, same reasoning as local-pickup's drawer:
    // keeps it out of any ancestor stacking context on desktop.
    const dialog =
      this.nextElementSibling instanceof HTMLElement && this.nextElementSibling.matches('.find-in-store-dialog')
        ? this.nextElementSibling
        : null;
    if (dialog) {
      this.#dialog = dialog;
      document.body.appendChild(dialog);
      this.#wireDialog(dialog);
    }

    this.#closestSection = this.closest('.shopify-section, dialog');
    this.#closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#dialog?.remove();
    this.#dialog = null;
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  /**
   * Re-fetches the section for the given variant and morphs in the fresh stocked-store list.
   * @param {string} variantId - The ID of the newly selected variant.
   */
  #fetchAvailability = (variantId) => {
    if (!variantId) return;

    const abortController = this.#createAbortController();
    const url = this.dataset.productUrl;

    fetch(`${url}?variant=${variantId}&section_id=${this.dataset.sectionId}`, {
      signal: abortController.signal,
    })
      .then((response) => response.text())
      .then((text) => {
        if (abortController.signal.aborted) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const wrapper = html.querySelector(`find-in-store[data-variant-id="${variantId}"]`);
        if (!wrapper) {
          this.setAttribute('hidden', '');
          return;
        }

        morph(this, wrapper);

        const newDialog =
          wrapper.nextElementSibling instanceof HTMLElement && wrapper.nextElementSibling.matches('.find-in-store-dialog')
            ? wrapper.nextElementSibling
            : null;
        if (newDialog && this.#dialog) {
          morph(this.#dialog, newDialog);
          this.#resetToDefaultState(this.#dialog);
        }
      })
      .catch((_e) => {
        if (abortController.signal.aborted) return;
        this.setAttribute('hidden', '');
      });
  };

  /**
   * Wires up the popup: list/detail view switching, the "Store info" → detail panel flow, and
   * the (optional) suburb/postcode search. Listeners are delegated from the dialog root rather
   * than bound to specific children, since a variant-change morph re-renders the list/detail
   * markup from the server and shouldn't need re-wiring.
   * @param {HTMLElement} dialog - The popup's `.find-in-store-dialog` element.
   */
  #wireDialog(dialog) {
    dialog.addEventListener('dialog:open', () => this.#onDialogOpen(dialog));
    dialog.addEventListener('dialog:close', () => this.#showList(dialog));

    dialog.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);

      const openButton = target.closest('[data-find-in-store-open-detail]');
      if (openButton instanceof HTMLElement) {
        this.#openDetail(dialog, openButton.dataset.storeHandle);
        return;
      }

      if (target.closest('[data-find-in-store-back]')) {
        // Unlike the dialog:close path (where the dialog itself is going away), the back button's
        // own header is CSS-hidden once the view switches back to the list — without moving focus
        // ourselves here, the now-detached back button would silently drop focus to <body>.
        this.#showList(dialog, { moveFocus: true });
        return;
      }
    });

    this.#wireSearch(dialog);
  }

  /**
   * Applies the reference site's default behaviour: as soon as the popup opens (and the
   * shopper hasn't searched yet), sort the list by distance from the current market's default
   * location (e.g. Sydney for AU, Auckland for NZ — set via data attributes rendered from
   * `localization.country.iso_code`, see `blocks/find-in-store.liquid`) and label the count
   * accordingly. This is plain haversine maths — it never needs Google Maps to load.
   * @param {HTMLElement} dialog - The popup's `.find-in-store-dialog` element.
   */
  #onDialogOpen(dialog) {
    const searchInput = dialog.querySelector('[data-find-in-store-search-input]');
    if (searchInput instanceof HTMLInputElement && searchInput.value.trim()) return;

    const lat = Number.parseFloat(this.dataset.defaultLat);
    const lng = Number.parseFloat(this.dataset.defaultLng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    this.#sortByDistance(dialog, { lat, lng }, this.dataset.defaultLocationLabel);
  }

  /**
   * Switches the popup back to the store list, hiding every detail panel.
   * @param {HTMLElement} dialog - The popup's `.find-in-store-dialog` element.
   * @param {{ moveFocus?: boolean }} [options] - Pass `moveFocus: true` when called from the
   *   back button, whose own header is about to be CSS-hidden — focus needs somewhere to go.
   *   Not needed for the dialog:close path, since the dialog itself is closing anyway.
   */
  #showList(dialog, { moveFocus = false } = {}) {
    dialog.setAttribute('data-view', 'list');
    for (const panel of dialog.querySelectorAll('[data-find-in-store-detail]')) {
      panel.setAttribute('hidden', '');
    }

    const heading = dialog.querySelector('.find-in-store-dialog__heading');
    const panel = dialog.querySelector('.find-in-store-dialog__panel');
    if (heading instanceof HTMLElement && panel instanceof HTMLElement) {
      panel.setAttribute('aria-labelledby', heading.id);
    }
    if (moveFocus && heading instanceof HTMLElement) heading.focus();
  }

  /**
   * Switches the popup to a single store's detail panel (address, phone, hours, map), lazily
   * upgrading its map from the plain iframe fallback to a real Google Map on first view.
   * @param {HTMLElement} dialog - The popup's `.find-in-store-dialog` element.
   * @param {string | undefined} handle - The store metaobject handle to show.
   */
  #openDetail(dialog, handle) {
    if (!handle) return;

    const panels = [...dialog.querySelectorAll('[data-find-in-store-detail]')];
    const matched = panels.find((panel) => panel instanceof HTMLElement && panel.dataset.storeHandle === handle);
    if (!(matched instanceof HTMLElement)) return;

    dialog.setAttribute('data-view', 'detail');
    for (const panel of panels) {
      panel.toggleAttribute('hidden', panel !== matched);
    }

    // The list-view heading this normally points at is `display: none` in detail view, which
    // would leave the dialog with no accessible name at all — point it at the visible store name
    // instead. #showList reverses this back to the list heading.
    const dialogPanel = dialog.querySelector('.find-in-store-dialog__panel');
    const nameHeading = matched.querySelector('.find-in-store-detail__name');
    if (dialogPanel instanceof HTMLElement && nameHeading instanceof HTMLElement && nameHeading.id) {
      dialogPanel.setAttribute('aria-labelledby', nameHeading.id);
    }

    const placeholder = matched.querySelector('[data-find-in-store-map-placeholder]');
    if (placeholder instanceof HTMLElement) this.#upgradeMap(placeholder);

    const backButton = dialog.querySelector('[data-find-in-store-back]');
    if (backButton instanceof HTMLElement) backButton.focus();
  }

  /**
   * Replaces a store detail panel's plain Google Maps iframe with a real `<store-detail-map>`
   * custom element (see `assets/store-detail-map.js`), so the marker can use the theme's
   * custom pin. Only runs once per placeholder, and only when this specific store is actually
   * viewed — never at page load or dialog open.
   * @param {HTMLElement} placeholder - The `[data-find-in-store-map-placeholder]` element.
   */
  #upgradeMap(placeholder) {
    if (placeholder.dataset.mapUpgraded === 'true') return;
    placeholder.dataset.mapUpgraded = 'true';

    const mapEl = document.createElement('store-detail-map');
    mapEl.className = placeholder.className;
    for (const attr of ['role', 'aria-label', 'data-lat', 'data-lng', 'data-title']) {
      const value = placeholder.getAttribute(attr);
      if (value != null) mapEl.setAttribute(attr, value);
    }

    const apiKey = placeholder.dataset.mapsApiKey;
    const markerIconUrl = placeholder.dataset.markerIconUrl;
    if (apiKey) mapEl.setAttribute('maps-api-key', apiKey);
    if (markerIconUrl) mapEl.setAttribute('marker-icon-url', markerIconUrl);

    mapEl.append(...placeholder.childNodes);
    placeholder.replaceWith(mapEl);
  }

  /**
   * Wires up the (optional) suburb/postcode search inside the popup, degrading to a plain
   * unsorted list when no Google Maps API key is configured or the Maps script fails to load.
   * Listeners are delegated from `dialog` so they survive the search input/listbox being
   * re-rendered by a variant-change morph.
   * @param {HTMLElement} dialog - The popup's `.find-in-store-dialog` element.
   */
  #wireSearch(dialog) {
    const apiKey = this.dataset.mapsApiKey;
    if (!apiKey) {
      dialog.setAttribute('data-maps-unavailable', '');
      return;
    }

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let debounceTimer;
    /** @type {any} */
    let sessionToken;
    /** @type {{ prediction: any, label: string }[]} */
    let suggestions = [];
    /** @type {number} The keyboard-highlighted suggestion, or -1 for none. */
    let activeIndex = -1;
    /** @type {Promise<void> | undefined} Lazily created on first search so Maps never loads on page load. */
    let mapsReady;
    /** @type {number} Bumped per request so a stale, out-of-order response can't overwrite a newer one. */
    let suggestionsRequestId = 0;

    const ensureMapsReady = () => {
      mapsReady ??= loadGoogleMaps(apiKey, { libraries: ['places'] }).catch((error) => {
        console.warn('[find-in-store] Failed to load Google Maps.', error);
        dialog.setAttribute('data-maps-unavailable', '');
      });
      return mapsReady;
    };

    const announce = (message) => {
      const status = dialog.querySelector('[data-find-in-store-suggestions-status]');
      if (status) status.textContent = message ?? '';
    };

    const clearSuggestions = () => {
      clearTimeout(debounceTimer);
      suggestions = [];
      activeIndex = -1;
      const listbox = dialog.querySelector('[data-find-in-store-suggestions]');
      const input = dialog.querySelector('[data-find-in-store-search-input]');
      if (listbox) {
        listbox.replaceChildren();
        listbox.hidden = true;
      }
      if (input) {
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }
    };

    const renderSuggestions = () => {
      const listbox = dialog.querySelector('[data-find-in-store-suggestions]');
      const input = dialog.querySelector('[data-find-in-store-search-input]');
      if (!listbox || !input || !suggestions.length) {
        clearSuggestions();
        announce(this.dataset.suggestionsNone);
        return;
      }
      listbox.replaceChildren(
        ...suggestions.map(({ label }, index) => {
          const option = document.createElement('li');
          option.id = `${listbox.id}-option-${index}`;
          option.className = 'find-in-store-dialog__suggestion';
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          option.textContent = label;
          return option;
        })
      );
      listbox.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      const template = this.dataset.suggestionsAvailableTemplate ?? '';
      announce(template.replace('__COUNT__', String(suggestions.length)));
    };

    /**
     * Moves the keyboard highlight through the suggestions, wrapping at both ends, mirroring
     * `assets/store-locator.js`'s `#moveActiveSuggestion`.
     * @param {number} delta - `1` for the next suggestion, `-1` for the previous.
     */
    const moveActiveSuggestion = (delta) => {
      const count = suggestions.length;
      const index = activeIndex === -1 ? (delta > 0 ? 0 : count - 1) : (activeIndex + delta + count) % count;

      const listbox = dialog.querySelector('[data-find-in-store-suggestions]');
      const input = dialog.querySelector('[data-find-in-store-search-input]');
      const options = listbox?.querySelectorAll('[role="option"]') ?? [];

      options.forEach((option, i) => {
        const isActive = i === index;
        option.classList.toggle('find-in-store-dialog__suggestion--active', isActive);
        option.setAttribute('aria-selected', String(isActive));
      });

      activeIndex = index;
      input?.setAttribute('aria-activedescendant', options[index]?.id ?? '');
      options[index]?.scrollIntoView({ block: 'nearest' });
    };

    const requestSuggestions = async (query) => {
      await ensureMapsReady();
      const AutocompleteSuggestion = window.google?.maps?.places?.AutocompleteSuggestion;
      if (!AutocompleteSuggestion) return;

      sessionToken ??= new google.maps.places.AutocompleteSessionToken();
      const requestId = ++suggestionsRequestId;

      try {
        const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          sessionToken,
          includedRegionCodes: [this.dataset.regionCode ?? 'au'],
          includedPrimaryTypes: ['locality', 'postal_code'],
        });

        // Ignore responses that arrive out of order behind a newer request.
        if (requestId !== suggestionsRequestId) return;

        suggestions = (results ?? [])
          .map((suggestion) => suggestion.placePrediction)
          .filter(Boolean)
          .map((prediction) => ({ prediction, label: prediction.text?.text ?? '' }));
        renderSuggestions();
      } catch (error) {
        if (requestId !== suggestionsRequestId) return;
        console.warn('[find-in-store] Autocomplete request failed.', error);
        clearSuggestions();
        announce(this.dataset.searchUnavailable);
      }
    };

    const selectSuggestion = async (index) => {
      const entry = suggestions[index];
      const input = dialog.querySelector('[data-find-in-store-search-input]');
      if (!entry) return;

      if (input instanceof HTMLInputElement) input.value = entry.label;
      clearSuggestions();

      try {
        const place = entry.prediction.toPlace();
        await place.fetchFields({ fields: ['location'] });
        if (!place.location) return;
        this.#sortByDistance(dialog, { lat: place.location.lat(), lng: place.location.lng() }, entry.label);
      } catch (error) {
        console.warn('[find-in-store] Could not resolve the selected place.', error);
      } finally {
        sessionToken = undefined;
      }
    };

    dialog.addEventListener('input', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-find-in-store-search-input]')) return;

      const query = input.value.trim();
      clearTimeout(debounceTimer);
      if (!query) {
        clearSuggestions();
        return;
      }
      debounceTimer = setTimeout(() => requestSuggestions(query), 250);
    });

    dialog.addEventListener('focusout', (event) => {
      const input = event.target;
      if (input instanceof HTMLInputElement && input.matches('[data-find-in-store-search-input]')) clearSuggestions();
    });

    dialog.addEventListener('keydown', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-find-in-store-search-input]')) return;
      if (!suggestions.length) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveActiveSuggestion(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveActiveSuggestion(-1);
          break;
        case 'Enter':
          // With nothing highlighted, Enter picks the top suggestion.
          event.preventDefault();
          selectSuggestion(activeIndex >= 0 ? activeIndex : 0);
          break;
        case 'Escape':
          // Prevent the browser's native search-input clear; just close the list, keep the text.
          // DialogComponent's own Escape handler is bound to this same `dialog` element (attached
          // later, on each showDialog() call), so stopImmediatePropagation is required here too —
          // same same-node-listener race as the suggestion click handler below.
          event.preventDefault();
          event.stopImmediatePropagation();
          clearSuggestions();
          break;
        default:
          break;
      }
    });

    dialog.addEventListener('mousedown', (event) => {
      if (/** @type {HTMLElement} */ (event.target).closest('[data-find-in-store-suggestions]')) event.preventDefault();
    });

    dialog.addEventListener('click', (event) => {
      const listbox = dialog.querySelector('[data-find-in-store-suggestions]');
      const option = /** @type {HTMLElement} */ (event.target).closest('[role="option"]');
      if (!option || !listbox || !listbox.contains(option)) return;

      // Selecting a suggestion removes it from the DOM synchronously (clearSuggestions), before
      // DialogComponent's own click listener — also bound to this same `dialog` element,
      // attached later on each showDialog() call — gets to run. That listener treats a
      // detached event.target as "clicked outside" and closes the popup. Listeners on the same
      // element still all fire on stopPropagation (it only blocks moving to the next node), so
      // stopImmediatePropagation is required to actually pre-empt that later listener.
      event.stopImmediatePropagation();

      const index = [...listbox.children].indexOf(option);
      if (index >= 0) selectSuggestion(index);
    });
  }

  /**
   * Resets the popup to its just-fetched default state: list view, no active search, no
   * distance sort — called after a variant-change morph replaces the list/detail markup.
   * @param {HTMLElement} dialog - The popup's `.find-in-store-dialog` element.
   */
  #resetToDefaultState(dialog) {
    this.#showList(dialog);
    const searchInput = dialog.querySelector('[data-find-in-store-search-input]');
    if (searchInput instanceof HTMLInputElement) searchInput.value = '';
  }

  /**
   * Reorders the rendered store rows by distance from the given origin and updates the count
   * text, mirroring `assets/store-locator.js`'s `#goToLocation`/`#sortByDistance`.
   * @param {HTMLElement} dialog - The popup's `.find-in-store-dialog` element.
   * @param {{ lat: number, lng: number }} origin - The point to measure distance from.
   * @param {string} [place] - A human-readable label for the origin, if known.
   */
  #sortByDistance(dialog, origin, place) {
    const list = dialog.querySelector('[data-find-in-store-list]');
    const dataScript = dialog.querySelector('[data-find-in-store-data]');
    if (!list || !dataScript) return;

    /** @type {{ handle: string, name: string, lat: number | null, lng: number | null }[]} */
    let stores = [];
    try {
      stores = JSON.parse(dataScript.textContent ?? '[]');
    } catch (error) {
      console.warn('[find-in-store] Could not parse store data.', error);
      return;
    }

    const distanceTemplate = this.dataset.distanceTemplate ?? '__DISTANCE__ km away';
    const items = [...list.querySelectorAll('[data-find-in-store-item]')];

    const withDistance = items.map((item) => {
      const handle = /** @type {HTMLElement} */ (item).dataset.storeHandle;
      const store = stores.find((candidate) => candidate.handle === handle);
      const distanceKm =
        store && typeof store.lat === 'number' && typeof store.lng === 'number'
          ? haversineDistanceKm(origin, { lat: store.lat, lng: store.lng })
          : Number.POSITIVE_INFINITY;
      return { item, distanceKm };
    });

    withDistance.sort((a, b) => a.distanceKm - b.distanceKm);

    for (const { item, distanceKm } of withDistance) {
      const distanceEl = item.querySelector('[data-find-in-store-distance]');
      if (distanceEl && Number.isFinite(distanceKm)) {
        const textEl = distanceEl.querySelector('[data-find-in-store-distance-text]');
        if (textEl) textEl.textContent = distanceTemplate.replace('__DISTANCE__', distanceKm.toFixed(1));
        distanceEl.hidden = false;
      }
      list.appendChild(item);
    }

    this.#updateCount(dialog, items.length, place);
  }

  /**
   * Picks the correctly-pluralized count template and fills in the real count/place, mirroring
   * `assets/store-locator.js`'s `#updateCount`.
   * @param {HTMLElement} dialog - The popup's `.find-in-store-dialog` element.
   * @param {number} count - The number of stores currently listed.
   * @param {string} [place] - A human-readable place label, if the count is location-scoped.
   */
  #updateCount(dialog, count, place) {
    const countEl = dialog.querySelector('[data-find-in-store-count]');
    if (!countEl) return;

    const isSingular = count === 1;
    const attribute = place
      ? isSingular
        ? 'countTemplateNearSingular'
        : 'countTemplateNearPlural'
      : isSingular
        ? 'countTemplateSingular'
        : 'countTemplatePlural';

    let template = this.dataset[attribute];
    if (!template) return;

    // The plural template is rendered with a literal placeholder count (2) so Shopify's `t`
    // filter resolves the "other" CLDR category — swap that placeholder for the real count.
    if (!isSingular) template = template.replace(/^\d+/, String(count));
    if (place) template = template.replace('__PLACE__', place);

    countEl.textContent = template;
  }
}

if (!customElements.get('find-in-store')) {
  customElements.define('find-in-store', FindInStore);
}
